/**
 * Leviathan Web - Tanks Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { GeometricPanel } from '@components/ui/GeometricPanel';
import { API_URL } from '@/config/api';

interface Tank {
  id: string;
  tankNumber: string;
  name: string;
  location: string | null;
  tankType: string;
  capacity: number;
  currentLevel: number;
  maxLevel: number | null;
  status: string;
  product: {
    id: string;
    name: string;
    sku: string;
  } | null;
}

interface Stats {
  total: number;
  active: number;
  maintenance: number;
  lowTanks: number;
  totalCapacity: number;
  totalLevel: number;
  utilizationPercent: number;
}

interface StatCardProps {
  label: string;
  value: string | number;
  status?: 'operational' | 'warning' | 'critical';
}

/**
 * Compact stat card for tank metrics
 */
function StatCard({ label, value, status }: StatCardProps) {
  const statusColor =
    status === 'operational'
      ? 'bg-success-green'
      : status === 'warning'
        ? 'bg-warning-amber'
        : status === 'critical'
          ? 'bg-error-red'
          : 'bg-steel';

  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />

      <div className="bg-panel-dark border border-panel-border p-4 clip-path-octagon relative">
        {status && (
          <div className="absolute top-3 right-3">
            <div
              className={`w-2 h-2 ${statusColor} rounded-full animate-pulse`}
              style={{ boxShadow: '0 0 8px currentColor' }}
            />
          </div>
        )}
        <div className="text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
          {label}
        </div>
        <div
          className="text-3xl font-mono font-bold text-amber"
          style={{
            textShadow: '0 0 10px rgba(255, 149, 0, 0.6)',
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

/**
 * Get tank type display info
 */
function getTankTypeInfo(type: string): { icon: string; label: string } {
  switch (type) {
    case 'ABOVE_GROUND':
      return { icon: 'AGT', label: 'Above Ground' };
    case 'UNDERGROUND':
      return { icon: 'UGT', label: 'Underground' };
    case 'PORTABLE':
      return { icon: 'PRT', label: 'Portable' };
    case 'TRAILER':
      return { icon: 'TRL', label: 'Trailer' };
    default:
      return { icon: 'TNK', label: 'Tank' };
  }
}

/**
 * Get status color
 */
function getStatusColor(status: string): string {
  switch (status) {
    case 'ACTIVE':
      return 'text-success-green';
    case 'MAINTENANCE':
      return 'text-warning-amber';
    case 'OUT_OF_SERVICE':
      return 'text-error-red';
    case 'DECOMMISSIONED':
      return 'text-text-tertiary';
    default:
      return 'text-text-secondary';
  }
}

/**
 * Get level bar color based on percentage
 */
function getLevelColor(percent: number): string {
  if (percent < 25) return 'bg-error-red';
  if (percent < 50) return 'bg-warning-amber';
  return 'bg-success-green';
}

const TanksPage = () => {
  const { accessToken } = useAuth();
  const [tanks, setTanks] = useState<Tank[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchTanks();
  }, [search, statusFilter, typeFilter]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tanks/stats`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchTanks = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('tankType', typeFilter);

      const response = await fetch(`${API_URL}/api/tanks?${params}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTanks(data.tanks);
      }
    } catch (error) {
      console.error('Failed to fetch tanks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setTypeFilter('');
  };

  const getLevelPercent = (tank: Tank) => {
    // Use maxLevel for percentage calculation (usable capacity), fall back to capacity
    const effectiveMax = tank.maxLevel ?? tank.capacity;
    return effectiveMax > 0 ? (tank.currentLevel / effectiveMax) * 100 : 0;
  };

  return (
    <div className="space-y-6 relative">
      {/* Header with geometric frame */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                Tanks
              </h1>
              <p className="text-text-secondary text-sm font-mono mt-1">
                Storage Tank Management
              </p>
            </div>
            <Link
              to="/tanks/new"
              className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-3 px-6 transition-all uppercase tracking-wider relative group"
              style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
            >
              <span className="relative z-10">+ New Tank</span>
              <div
                className="absolute inset-0 bg-gradient-to-r from-amber to-steel opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ mixBlendMode: 'overlay' }}
              />
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard label="Total Tanks" value={stats.total} />
          <StatCard label="Active" value={stats.active} status="operational" />
          <StatCard
            label="Maintenance"
            value={stats.maintenance}
            status={stats.maintenance > 0 ? 'warning' : undefined}
          />
          <StatCard
            label="Low Level"
            value={stats.lowTanks}
            status={stats.lowTanks > 0 ? 'critical' : undefined}
          />
          <StatCard label="Utilization" value={`${stats.utilizationPercent}%`} />
        </div>
      )}

      {/* Filters Panel */}
      <GeometricPanel>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Search
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tank #, name, location..."
              className="form-input"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-input"
            >
              <option value="">All Statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="MAINTENANCE">Maintenance</option>
              <option value="OUT_OF_SERVICE">Out of Service</option>
              <option value="DECOMMISSIONED">Decommissioned</option>
            </select>
          </div>
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Tank Type
            </label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="form-input"
            >
              <option value="">All Types</option>
              <option value="ABOVE_GROUND">Above Ground</option>
              <option value="UNDERGROUND">Underground</option>
              <option value="PORTABLE">Portable</option>
              <option value="TRAILER">Trailer</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full bg-panel-border hover:bg-steel/20 text-text-primary font-mono py-2 px-4 transition-all uppercase text-xs tracking-wider border border-panel-border hover:border-steel"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </GeometricPanel>

      {/* Tank Cards Grid */}
      {isLoading ? (
        <div className="p-12 text-center">
          <div className="text-text-tertiary font-mono animate-pulse">
            &gt; Loading tanks...
          </div>
        </div>
      ) : tanks.length === 0 ? (
        <div className="p-12 text-center">
          <div className="text-text-tertiary font-mono text-lg mb-2">
            &gt; No tanks found
          </div>
          <p className="text-text-tertiary font-mono text-sm">
            {search || statusFilter || typeFilter
              ? 'Try adjusting your search filters'
              : 'Add your first storage tank to get started'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tanks.map((tank) => {
            const levelPercent = getLevelPercent(tank);
            const tankType = getTankTypeInfo(tank.tankType);

            return (
              <Link key={tank.id} to={`/tanks/${tank.id}`} className="relative stat-card group">
                <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
                <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
                <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
                <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

                <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon hover:border-amber/60 transition-all">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-mono bg-panel-border px-2 py-1 text-steel">
                          {tankType.icon}
                        </span>
                        <span className="text-text-primary font-mono font-semibold text-lg">
                          {tank.tankNumber}
                        </span>
                      </div>
                      <div className="text-text-secondary font-mono text-sm mt-1">{tank.name}</div>
                    </div>
                    <span
                      className={`${getStatusColor(tank.status)} font-mono text-xs uppercase tracking-wider font-semibold`}
                      style={{ textShadow: '0 0 6px currentColor' }}
                    >
                      {tank.status.replace('_', ' ')}
                    </span>
                  </div>

                  {/* Product */}
                  <div className="mb-4">
                    <span className="text-text-tertiary font-mono text-xs uppercase tracking-widest">
                      Product
                    </span>
                    <div className="text-text-primary font-mono">
                      {tank.product?.name || (
                        <span className="text-text-tertiary italic">Empty</span>
                      )}
                    </div>
                  </div>

                  {/* Level Indicator */}
                  <div className="mb-4">
                    <div className="flex justify-between text-xs font-mono mb-1">
                      <span className="text-text-tertiary uppercase tracking-widest">Level</span>
                      <span
                        className="text-amber"
                        style={{ textShadow: '0 0 6px rgba(255, 149, 0, 0.4)' }}
                      >
                        {levelPercent.toFixed(1)}%
                      </span>
                    </div>
                    <div className="h-4 bg-background border border-panel-border overflow-hidden">
                      <div
                        className={`h-full ${getLevelColor(levelPercent)} transition-all`}
                        style={{ width: `${levelPercent}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs font-mono mt-1 text-text-tertiary">
                      <span>{tank.currentLevel.toLocaleString()} gal</span>
                      <span>{tank.capacity.toLocaleString()} gal</span>
                    </div>
                  </div>

                  {/* Location */}
                  {tank.location && (
                    <div className="text-text-tertiary font-mono text-xs">
                      LOC: {tank.location}
                    </div>
                  )}

                  {/* Hover Arrow */}
                  <div className="absolute bottom-4 right-4 text-steel opacity-0 group-hover:opacity-100 transition-opacity font-mono">
                    &rarr;
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TanksPage;
